import { useCallback, useEffect, useState } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { useRoleDataScope } from '../commons/useRoleDataScope';
import { precisaAcaoDeLance } from '../commons/estrategiaContemplacao';

/**
 * Aviso de assembleia chegando (pedido do Rick, 30/09/2026), no sino e no
 * topo do Feed: cotas do escopo com assembleia em até DIAS_AVISO dias e ainda
 * sem lance. Sai sozinho, sem cron: é calculado das vendas toda vez que o
 * sino ou o Feed carregam. O vendedor precisa perguntar ao cliente se ele quer lance livre
 * ANTES da véspera — é na véspera (e no dia, se ainda faltar) que o robô
 * registra o fixo automático (ver AvisoLanceAutomatico), e a cota só tem um
 * lance por assembleia.
 *
 * "Sem lance" segue a régua da FaixaAssembleias (migration 89): status nulo,
 * failed ou cancelled. Não é post no banco: sai das vendas de quem está vendo.
 * Desde a 237 a estratégia de contemplação entra: "Só sorteio" não conta, e
 * "Lance fixo" só conta quando o robô não resolveu (ver precisaAcaoDeLance).
 */

/** Uma semana antes, sempre (Rick, 30/09): dá tempo de falar com o cliente. */
export const DIAS_AVISO = 7;
const SEM_LANCE = new Set(['failed', 'cancelled']);
const CHAVE_VISTOS = 'north.avisos-assembleia.vistos';

export type VendaAviso = {
  next_assembleia_date: string | null;
  next_lance_status: string | null;
  lance_fixo_automatico: boolean | null;
  /** Migration 237. Ausente = 'nenhuma' (comportamento de antes). */
  estrategia_contemplacao?: string | null;
};

export type AvisoAssembleia = {
  /** Estável por assembleia: é o que o "visto" guarda. */
  id: string;
  data: string; // YYYY-MM-DD
  semLance: number;
  titulo: string;
  texto: string;
};

const DIA_SEM = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const utc = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
const somaDias = (iso: string, n: number) => new Date(utc(iso) + n * 864e5).toISOString().slice(0, 10);
const rotulo = (iso: string) => `${DIA_SEM[new Date(utc(iso)).getUTCDay()]} ${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
export const isoLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Um aviso por dia de assembleia, só quando há cota sem lance. */
export function avisosDeAssembleia(vendas: VendaAviso[], hoje: string): AvisoAssembleia[] {
  const ate = somaDias(hoje, DIAS_AVISO);
  const porDia = new Map<string, { auto: number; manual: number }>();
  for (const v of vendas) {
    const data = v.next_assembleia_date?.slice(0, 10);
    if (!data || data < hoje || data > ate) continue;
    if (v.next_lance_status != null && !SEM_LANCE.has(v.next_lance_status)) continue;
    // Estratégia (237): sorteio não quer lance; lance fixo é do robô até ele
    // falhar ou chegar o dia — mesma régua da fila (precisaAcaoDeLance).
    const dias = Math.round((utc(data) - utc(hoje)) / 864e5);
    if (!precisaAcaoDeLance(v.estrategia_contemplacao, v.lance_fixo_automatico, v.next_lance_status, { diasAteAssembleia: dias })) {
      continue;
    }
    const c = porDia.get(data) ?? { auto: 0, manual: 0 };
    if (v.lance_fixo_automatico) c.auto += 1;
    else c.manual += 1;
    porDia.set(data, c);
  }
  return [...porDia.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([data, { auto, manual }]) => {
      const semLance = auto + manual;
      const vespera = somaDias(data, -1);
      const quando = vespera <= hoje ? 'hoje' : `na véspera (${rotulo(vespera)})`;
      const partes = [`${semLance} ${semLance === 1 ? 'cota sem lance' : 'cotas sem lance'}.`];
      if (auto > 0) {
        partes.push(`Confirme se o cliente deseja lance livre; caso contrário, o lance fixo automático será registrado ${quando}.`);
      }
      if (manual > 0) {
        partes.push(
          `${manual} sem lance automático: sem registro manual, ${manual === 1 ? 'permanece' : 'permanecem'} sem lance.`,
        );
      }
      return {
        id: `assembleia:${data}`,
        data,
        semLance,
        titulo: data === hoje ? 'A assembleia é hoje' : `Próxima assembleia: ${rotulo(data)}`,
        texto: partes.join(' '),
      };
    });
}

const lerVistos = (): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(CHAVE_VISTOS) ?? '[]'));
  } catch {
    return new Set();
  }
};

/** Os avisos do escopo de quem está vendo + quais já foram vistos neste aparelho. */
export function useAvisosAssembleia({ ativo = true }: { ativo?: boolean } = {}) {
  const { isLoading, isSuperAdmin, scopedCrmUserIds } = useRoleDataScope();
  const [avisos, setAvisos] = useState<AvisoAssembleia[]>([]);
  const [vistos, setVistos] = useState<Set<string>>(lerVistos);
  const escopoKey = scopedCrmUserIds.join(',');

  const recarregar = useCallback(async () => {
    if (!ativo || isLoading) return;
    const hoje = isoLocal(new Date());
    let q = supabase
      .from('servopa_vendas_with_details')
      .select('next_assembleia_date, next_lance_status, lance_fixo_automatico, estrategia_contemplacao')
      .eq('status', 'confirmed')
      .eq('contemplado', false)
      .gte('next_assembleia_date', hoje)
      .lte('next_assembleia_date', somaDias(hoje, DIAS_AVISO));
    if (!isSuperAdmin) {
      q = scopedCrmUserIds.length > 0 ? q.in('sales_person_id', scopedCrmUserIds) : q.eq('sales_person_id', -1);
    }
    const { data } = await q.returns<VendaAviso[]>();
    setAvisos(avisosDeAssembleia(data ?? [], hoje));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo, isLoading, isSuperAdmin, escopoKey]);

  useEffect(() => {
    recarregar().catch(() => setAvisos([]));
    const aoFocar = () => recarregar().catch(() => {});
    window.addEventListener('focus', aoFocar);
    return () => window.removeEventListener('focus', aoFocar);
  }, [recarregar]);

  const marcarVistos = useCallback((ids: string[]) => {
    const hoje = isoLocal(new Date());
    // Só guarda o que ainda pode aparecer: assembleia passada sai da lista.
    const novos = new Set([...lerVistos(), ...ids].filter((id) => id.slice('assembleia:'.length) >= hoje));
    try {
      localStorage.setItem(CHAVE_VISTOS, JSON.stringify([...novos]));
    } catch {
      // Navegador sem armazenamento: o aviso só volta a aparecer.
    }
    setVistos(novos);
  }, []);

  const naoVistos = avisos.filter((a) => !vistos.has(a.id));
  return { avisos, naoVistos, marcarVistos };
}
